import { Search } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

interface SearchBarProps {
  /**
   * Current search text value
   */
  value: string
  /**
   * Callback when search text changes
   */
  onChange: (value: string) => void
  /**
   * Placeholder text for the search input
   */
  placeholder?: string
  /**
   * Accessible label for screen readers
   */
  ariaLabel?: string
  /**
   * Additional CSS classes
   */
  className?: string
  /**
   * Whether to show the search bar (for conditional rendering)
   */
  show?: boolean
}

/**
 * Reusable search bar component with icon and consistent styling.
 * Used across archive pages for filtering content.
 *
 * @example
 * ```tsx
 * <SearchBar
 *   value={searchText}
 *   onChange={setSearchText}
 *   placeholder="Search moves..."
 *   ariaLabel="Search moves"
 *   show={!isEmpty}
 * />
 * ```
 */
export function SearchBar({
  value,
  onChange,
  placeholder = 'Search...',
  ariaLabel = 'Search',
  className,
  show = true,
}: SearchBarProps) {
  if (!show) {
    return null
  }

  return (
    <div className={cn('relative', className)}>
      <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#666666]" />
      <Input
        type="search"
        placeholder={placeholder}
        value={value}
        onChange={e => onChange(e.target.value)}
        className="pl-10 pr-4"
        aria-label={ariaLabel}
      />
    </div>
  )
}
